import { MouseEvent, MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react';

const INITIAL_WINDOW_CLOSE_STATE = { isOpen: false, isPermanent: false, isHovering: false };
const INITIAL_WINDOW_OPEN_STATE = { isOpen: true, isPermanent: true, isHovering: false };

// A recursive function that returns the window element that the mouse is over
// (or null if the mouse is not over a window)
const getMouseOverWindow = (element: Element, root: Element) => {
  // Base case: We are outside the pdp window root or we are outside the document
  if (!element || !root.contains(element)) return null;

  if (element.getAttribute('data-qa')?.includes('window')) {
    return element;
  }

  return getMouseOverWindow(element.parentElement, root);
};

export const useWindowState = (pdpId = '', defaultOpen = false) => {
  const [windowState, setWindowState] = useState(
    defaultOpen ? INITIAL_WINDOW_OPEN_STATE : INITIAL_WINDOW_CLOSE_STATE,
  );

  // We need to keep track of the window element that the mouse is over so that
  // we keep the window open when the mouse continues over it
  // and can close the window when the mouse leaves it
  const windowRef = useRef(null);
  const windowMouseLeaveHandler = useCallback(
    (e) => {
      if (windowState.isPermanent) return;
      if (e.relatedTarget?.getAttribute('data-pdp-id') === pdpId) return;
      setWindowState({ ...windowState, isOpen: false, isHovering: false });
    },
    [windowState, pdpId],
  );

  useEffect(() => {
    if (windowState.isPermanent) {
      windowRef.current?.removeEventListener('mouseleave', windowMouseLeaveHandler);
      windowRef.current = null;
    }

    if (windowState.isOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      windowRef.current?.removeEventListener('mouseleave', windowMouseLeaveHandler);
      document.body.style.overflow = 'unset';
    };
  }, [windowState]);

  const handleLinkClick = () => {
    setWindowState({ isOpen: true, isPermanent: true, isHovering: false });
  };

  const handleLinkClose = () => {
    setWindowState(INITIAL_WINDOW_CLOSE_STATE);
  };

  const handleLinkMouseEnter = () => {
    setWindowState({ ...windowState, isOpen: true, isHovering: true });
  };

  const handleLinkMouseLeave = (e: MouseEvent<HTMLElement>): MouseEventHandler<HTMLElement> => {
    if (windowState.isPermanent) {
      return;
    }

    // Check if we are leaving the link to the window
    if (pdpId !== '') {
      const pdpWrapper = document.querySelector('#pdp-wrapper');
      const hoveredElement = document.elementFromPoint(e.clientX, e.clientY);

      // If we are leaving the link to the window itself, we don't want to close the window
      const window = getMouseOverWindow(hoveredElement, pdpWrapper);
      if (window) {
        windowRef.current = window;
        window.addEventListener('mouseleave', windowMouseLeaveHandler);
        return;
      }
    }

    setWindowState(INITIAL_WINDOW_CLOSE_STATE);
  };

  return {
    windowState,
    handleLinkClick,
    handleLinkClose,
    handleLinkMouseEnter,
    handleLinkMouseLeave,
  };
};
